import React, { useState, useMemo, useContext, useEffect, useRef } from 'react';
import { Search, Clock, Zap, Shield } from 'lucide-react';
import { CartContext } from '../../../context/CartContext';
import RecentOrdersModal from '../Modals/RecentOrdersModal';
import ShortcutConfirmModal from '../Modals/ShortcutConfirmModal';
import VariantSelectionModal from '../Modals/VariantSelectionModal';
import SpecialInstructionsModal from '../Modals/SpecialInstructionsModal';
import OpenItemModal from '../Modals/OpenItemModal';
import { useMenuItems } from '../../../hooks/useMenuItems';
import toast from 'react-hot-toast';

export default function MenuSection_design1({ browseMode = false, onAddToCart: externalAddToCart, isCounterMode = false }) {
  const { getCurrentCartItems, getRecentOrders, repeatOrder, addToCart: contextAddToCart, currentTableId, isBillPrinted } =
    useContext(CartContext);

  const addToCart = externalAddToCart || contextAddToCart;
  const { items: apiItems, loading, error, refreshItems } = useMenuItems();

  const [menuItems, setMenuItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [showRecentOrders, setShowRecentOrders] = useState(false);
  const [showVariantModal, setShowVariantModal] = useState(false);
  const [selectedItemForVariant, setSelectedItemForVariant] = useState(null);
  const [pendingItem, setPendingItem] = useState(null);
  const [openItemForModal, setOpenItemForModal] = useState(null);
  const [quickAddMode, setQuickAddMode] = useState(() => {
    const saved = localStorage.getItem('pos_quickAddMode');
    return saved ? JSON.parse(saved) : true;
  });
  const [shortcutBuffer, setShortcutBuffer] = useState('');
  const [showShortcutIndicator, setShowShortcutIndicator] = useState(false);
  const [highlightedShortcut, setHighlightedShortcut] = useState(null);
  const [confirmItem, setConfirmItem] = useState(null);
  const [timeoutProgress, setTimeoutProgress] = useState(100);

  const timeoutRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const bufferRef = useRef('');

  useEffect(() => {
    if (Array.isArray(apiItems) && apiItems.length > 0) {
      const mapped = apiItems.map((item, index) => ({
        ...item,
        price: item.basePrice || 0,
        favorite: item.isFavourite || false,
        shortcutKey: (index + 1).toString(),
        customizable: true,
        station: item.station || 'kitchen',
      }));
      setMenuItems(mapped);
    }
  }, [apiItems]);

  useEffect(() => {
    localStorage.setItem('pos_quickAddMode', JSON.stringify(quickAddMode));
  }, [quickAddMode]);

  const categories = useMemo(() => {
    const allCategories = menuItems.map((item) => item.category).filter(Boolean);
    return ['All', 'Favorites', ...new Set(allCategories)];
  }, [menuItems]);

  const toggleFavorite = (itemId) => {
    setMenuItems((currentItems) =>
      currentItems.map((item) =>
        item.id === itemId ? { ...item, favorite: !item.favorite } : item
      )
    );
  };

  const filteredItems = useMemo(() => {
    return menuItems.filter((item) => {
      const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase());
      let matchesCategory =
        selectedCategory === 'All'
          ? true
          : selectedCategory === 'Favorites'
          ? item.favorite
          : item.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [selectedCategory, searchQuery, menuItems]);

  const cartItems = getCurrentCartItems() || [];
  const cartItemsMap = cartItems
    .filter((item) => !item.sentToKitchen)
    .reduce((acc, item) => {
      acc[item.id] = item;
      return acc;
    }, {});

  const recentOrders = getRecentOrders();
  const showInstructionPopupOnAdd = false;
  const defaultCustomization = { customInstructions: '' };

  const clearShortcutBuffer = () => {
    bufferRef.current = '';
    setShortcutBuffer('');
    setShowShortcutIndicator(false);
    setHighlightedShortcut(null);
    setTimeoutProgress(100);
    clearTimeout(timeoutRef.current);
    clearInterval(progressIntervalRef.current);
  };

  const processShortcut = (shortcut) => {
    if (isBillPrinted) {
      toast.error('Bill already printed. Order is locked.');
      clearShortcutBuffer();
      return;
    }
    const item = menuItems.find((item) => item.shortcutKey === shortcut);
    if (!item) return clearShortcutBuffer();
    clearShortcutBuffer();
    if (!currentTableId) return;

    if (item.isOpenItem) {
      setOpenItemForModal(item);
      return;
    }
    const hasVariants = item.variants && item.variants.length > 0;
    if (hasVariants) {
      handleItemClick(item);
      return;
    }
    if (quickAddMode) {
      if (showInstructionPopupOnAdd) setPendingItem({ item, showToast: true });
      else addToCart(item, defaultCustomization, true);
    } else {
      setConfirmItem(item);
    }
  };

  const handleConfirmAdd = () => {
    if (isBillPrinted) {
      toast.error('Bill already printed. Order is locked.');
      setConfirmItem(null);
      return;
    }
    if (confirmItem) {
      if (showInstructionPopupOnAdd) setPendingItem({ item: confirmItem, showToast: true });
      else addToCart(confirmItem, defaultCustomization, true);
      setConfirmItem(null);
    }
  };

  const handleCancelAdd = () => setConfirmItem(null);

  const handleOpenItemConfirm = ({ price, customName }) => {
    if (!openItemForModal) return;
    const item = openItemForModal;
    const openItem = {
      ...item,
      price,
      basePrice: price,
      name: customName || item.name,
      openItemOriginalName: item.name,
    };
    addToCart(openItem, defaultCustomization);
    toast.success(`${customName || item.name} added — ₹${price}`, { duration: 2000 });
    setOpenItemForModal(null);
  };

  const handleItemClick = (item) => {
    if (isBillPrinted) {
      toast.error('Bill already printed. Order is locked.');
      return;
    }
    if (item.isOpenItem) {
      setOpenItemForModal(item);
      return;
    }
    const hasVariants = item.variants && item.variants.length > 0;
    if (hasVariants) {
      setSelectedItemForVariant(item);
      setShowVariantModal(true);
    } else if (showInstructionPopupOnAdd) {
      setPendingItem({ item, showToast: false });
    } else {
      addToCart(item, defaultCustomization);
      toast.success(`${item.name} added`, { duration: 2000 });
    }
  };

  const handleSelectVariant = (variant) => {
    if (isBillPrinted) {
      toast.error('Bill already printed. Order is locked.');
      return;
    }
    if (!selectedItemForVariant) return;
    const itemWithVariant = {
      ...selectedItemForVariant,
      menuItemId: selectedItemForVariant.id,
      variantId: variant.id,
      variantName: variant.variantName,
      price: variant.variantPrice,
      finalPrice: variant.variantPrice,
    };
    setShowVariantModal(false);
    setSelectedItemForVariant(null);
    if (showInstructionPopupOnAdd) {
      setPendingItem({ item: itemWithVariant, showToast: false });
    } else {
      addToCart(itemWithVariant, defaultCustomization);
      const label = itemWithVariant.variantName ? `${itemWithVariant.name} (${itemWithVariant.variantName})` : itemWithVariant.name;
      toast.success(`${label} added`, { duration: 2000 });
    }
  };

  const handleInstructionsSave = (instructions) => {
    if (!pendingItem) return;
    const { item, showToast } = pendingItem;
    addToCart(item, { customInstructions: instructions || '' }, showToast);
    if (!showToast) {
      const label = item.variantName ? `${item.name} (${item.variantName})` : item.name;
      toast.success(`${label} added`, { duration: 2000 });
    }
    setPendingItem(null);
  };

  const handleInstructionsClose = () => {
    if (!pendingItem) return;
    const { item, showToast } = pendingItem;
    addToCart(item, defaultCustomization, showToast);
    if (!showToast) {
      const label = item.variantName ? `${item.name} (${item.variantName})` : item.name;
      toast.success(`${label} added`, { duration: 2000 });
    }
    setPendingItem(null);
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (browseMode) return;
      if (
        e.target.tagName === 'INPUT' ||
        e.target.tagName === 'TEXTAREA' ||
        e.target.isContentEditable ||
        showRecentOrders ||
        confirmItem ||
        showVariantModal ||
        pendingItem ||
        openItemForModal
      ) return;

      if (e.key >= '0' && e.key <= '9') {
        e.preventDefault();
        const newBuffer = bufferRef.current + e.key;
        bufferRef.current = newBuffer;
        setShortcutBuffer(newBuffer);
        setShowShortcutIndicator(true);
        setHighlightedShortcut(newBuffer);
        clearTimeout(timeoutRef.current);
        clearInterval(progressIntervalRef.current);

        const exactMatch = menuItems.find((item) => item.shortcutKey === newBuffer);
        const hasPartialMatches = menuItems.some((item) => item.shortcutKey?.startsWith(newBuffer));

        if (exactMatch && !hasPartialMatches) {
          setTimeout(() => processShortcut(newBuffer), 100);
        } else if (exactMatch || hasPartialMatches) {
          setTimeoutProgress(100);
          const startTime = Date.now();
          const duration = 1000;
          progressIntervalRef.current = setInterval(() => {
            const elapsed = Date.now() - startTime;
            const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
            setTimeoutProgress(remaining);
            if (remaining === 0) clearInterval(progressIntervalRef.current);
          }, 16);
          timeoutRef.current = setTimeout(() => {
            processShortcut(bufferRef.current);
          }, 1000);
        } else {
          clearShortcutBuffer();
        }
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
      clearShortcutBuffer();
    };
  }, [menuItems, quickAddMode, showRecentOrders, confirmItem, showVariantModal, pendingItem, currentTableId, browseMode]);

  return (
    <div className="flex flex-col h-full bg-white">
      {/* ===== HEADER (search + red action buttons) ===== */}
      <div className="border-b border-slate-300 px-6 py-3">
        <div className="flex gap-3 items-center">
          {/* Search Bar */}
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
            <input
              type="text"
              placeholder="Search menu items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-300 hover:border-slate-400 focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 text-sm rounded-lg"
            />
          </div>

          {/* ===== RED ACTION BUTTONS (PetPooja-style) ===== */}
          <div className="flex items-center gap-2 ml-auto">
            {/* Quick Add / Safe Mode toggle */}
            {!browseMode && (
              <button
                onClick={() => setQuickAddMode(!quickAddMode)}
                className={`
                  flex flex-col items-center justify-center min-w-[64px] px-3 py-1.5 rounded-md transition-all
                  shadow-sm hover:shadow-md
                  ${quickAddMode
                    ? 'bg-gradient-to-b from-red-500 to-red-600 hover:from-red-600 hover:to-red-700'
                    : 'bg-gradient-to-b from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700'
                  }
                `}
                title={quickAddMode ? 'Quick Add Mode' : 'Safe Mode'}
              >
                {quickAddMode ? (
                  <Zap size={18} className="text-white" fill="white" strokeWidth={2} />
                ) : (
                  <Shield size={18} className="text-white" strokeWidth={2} />
                )}
                <span className="text-[10px] font-semibold text-white mt-0.5 tracking-wide">
                  {quickAddMode ? 'Quick' : 'Safe'}
                </span>
              </button>
            )}

            {/* Refresh */}
            <button
              onClick={refreshItems}
              className="group flex flex-col items-center justify-center min-w-[64px] px-3 py-1.5 rounded-md bg-gradient-to-b from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 shadow-sm hover:shadow-md transition-all"
              title="Refresh menu items"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="transition-transform duration-500 group-hover:rotate-180"
              >
                <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                <path d="M21 3v5h-5" />
                <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                <path d="M8 16H3v5" />
              </svg>
              <span className="text-[10px] font-semibold text-white mt-0.5 tracking-wide">
                Refresh
              </span>
            </button>

            {/* Recent Orders */}
            <button
              onClick={() => setShowRecentOrders(true)}
              className="flex flex-col items-center justify-center min-w-[64px] px-3 py-1.5 rounded-md bg-gradient-to-b from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 shadow-sm hover:shadow-md transition-all"
              title="View recent orders"
            >
              <Clock size={18} className="text-white" strokeWidth={2} />
              <span className="text-[10px] font-semibold text-white mt-0.5 tracking-wide">
                Recent
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* ===== MAIN CONTENT: SIDEBAR + ITEMS GRID ===== */}
      <div className="flex flex-1 overflow-hidden">

        {/* LEFT SIDEBAR */}
        <div className="w-44 flex-shrink-0 bg-white border-r border-slate-200 overflow-y-auto">
          <style>{`
            .cat-sidebar::-webkit-scrollbar { width: 4px; }
            .cat-sidebar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 2px; }
          `}</style>
          <div className="cat-sidebar flex flex-col">
            {categories.map((category) => {
              const isActive = selectedCategory === category;
              return (
                <button
                  key={category}
                  onClick={() => {
                    setSelectedCategory(category);
                    setSearchQuery('');
                  }}
                  className={`
                    relative text-left px-4 py-3 text-sm font-semibold transition-all border-b border-slate-100
                    ${isActive
                      ? 'bg-red-50 text-red-700'
                      : 'text-slate-700 hover:bg-slate-50'
                    }
                  `}
                >
                  {isActive && (
                    <span className="absolute left-0 top-0 bottom-0 w-1 bg-red-600" />
                  )}
                  {category}
                </button>
              );
            })}
          </div>
        </div>

        {/* ITEMS GRID */}
        <div className="flex-1 overflow-y-auto p-4 bg-slate-50">
          {loading ? (
            <p className="text-center text-slate-500 mt-10">Loading menu items...</p>
          ) : error ? (
            <p className="text-center text-red-500 mt-10">Failed to load menu items: {error}</p>
          ) : filteredItems.length > 0 ? (
            <div className="grid grid-cols-4 gap-3">
              {filteredItems.map((item) => {
                const cartItem = cartItemsMap[item.id];
                const isHighlighted = highlightedShortcut && item.shortcutKey?.startsWith(highlightedShortcut);
                const inCart = !!cartItem;

                return (
                  <button
                    key={item.id}
                    onClick={() => handleItemClick(item)}
                    disabled={isBillPrinted}
                    className={`
                      group relative bg-white rounded border text-left transition-all overflow-hidden
                      ${isHighlighted
                        ? 'border-red-500 ring-2 ring-red-300 shadow-md'
                        : inCart
                        ? item.isVeg
                          ? 'border-green-500 shadow-sm'
                          : 'border-red-500 shadow-sm'
                        : 'border-slate-200 hover:border-red-400 hover:shadow-sm'
                      }
                      ${isBillPrinted ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                    `}
                  >
                    {/* Stripe only when in cart - veg/non-veg colored */}
                    {inCart && (
                      <div className={`absolute left-0 top-0 bottom-0 w-1 ${
                        item.isVeg ? 'bg-green-500' : 'bg-red-500'
                      }`} />
                    )}

                    {/* Quantity badge - veg/non-veg colored */}
                    {inCart && (
                      <div className={`absolute top-1 right-1 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center ${
                        item.isVeg ? 'bg-green-600' : 'bg-red-600'
                      }`}>
                        {cartItem.quantity}
                      </div>
                    )}

                    <div className="px-3 py-4 min-h-[64px] flex items-center">
                      <span className="text-sm font-semibold text-slate-800 leading-tight">
                        {item.name}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-slate-500 font-semibold">No items found</p>
            </div>
          )}
        </div>
      </div>

      {/* ===== MODALS ===== */}
      {showRecentOrders && (
        <RecentOrdersModal
          currentTableId={currentTableId}
          onClose={() => setShowRecentOrders(false)}
          onAddToCart={(items) => {
            if (items && items.length > 0) {
              const expandedItems = [];
              items.forEach(item => {
                const quantity = item.quantity || 1;
                for (let i = 0; i < quantity; i++) {
                  expandedItems.push({
                    id: item.id,
                    name: item.name,
                    price: item.price,
                    finalPrice: item.price,
                    variantId: item.variantId,
                    variantName: item.variantName,
                    customization: item.customization,
                    quantity: 1,
                    sentToKitchen: false,
                  });
                }
              });
              repeatOrder({ items: expandedItems });
            }
            setShowRecentOrders(false);
          }}
        />
      )}

      {confirmItem && (
        <ShortcutConfirmModal item={confirmItem} onConfirm={handleConfirmAdd} onCancel={handleCancelAdd} />
      )}

      {showVariantModal && selectedItemForVariant && (
        <VariantSelectionModal
          item={selectedItemForVariant}
          onSelectVariant={handleSelectVariant}
          onClose={() => {
            setShowVariantModal(false);
            setSelectedItemForVariant(null);
          }}
        />
      )}

      {pendingItem && (
        <SpecialInstructionsModal
          item={pendingItem.item}
          onSave={handleInstructionsSave}
          onClose={handleInstructionsClose}
        />
      )}

      {openItemForModal && (
        <OpenItemModal
          item={openItemForModal}
          onConfirm={handleOpenItemConfirm}
          onClose={() => setOpenItemForModal(null)}
        />
      )}
    </div>
  );
}